import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import { ImageViewer, NoteImage, NoteVoice } from './NoteMedia';
import { ChecklistIcon } from './TodoNotes';
import { VoiceBar, canRecord, useVoiceRecorder } from '../VoiceNote';
import { fileSize } from '../ChatFile';
import { MAX_LINES, audioOf, imagesOf } from './notes';
import './TodoParts.css';

/*
 * A TO-DO'S CHECKLIST AND FILES.
 *
 * Gordon: "when I convert a note into a to-do item it loses all the details,
 * attachments, etc. Please fix as it doesn't make sense."
 *
 * The server never lost them - a note becomes a to-do in place, the same row
 * with its kind flipped - but nothing on a to-do's screens drew a checklist
 * or a file, so the moment a note was a to-do its lines, its photos and its
 * voice notes were gone as far as anybody could see. ("Checklist from text",
 * from the AI help, filled the same invisible checklist on a to-do.)
 *
 * So a to-do shows them, wherever its details are open: the panel from its
 * comments button, under its editor, and in a board card's popup. Ticked a
 * line at a time, as a note's are (the per-line routes, so two people on a
 * shared list do not undo each other's ticks); retyped in place; a line
 * added at the foot; photos shown and opened large, voice notes played,
 * files downloaded - and more of any of them added here too.
 *
 * `on` is TodoApp's: tick, lineText, addLine, delLine, upload,
 * removeAttachment - each a promise. `load` fetches a file's bytes with the
 * sign-in, `download` saves one, `canRemove(att)` says whether this person
 * may take a file off (whoever put it on, or the list's owner).
 * `readOnly` for a to-do that is somebody else's now (Delegated).
 *
 * ATTACHING (Oct 2026). Gordon: a note takes a photo, a voice note or a
 * file in one press, and a to-do should too - the same three, in the same
 * places. So the files area opens with an ATTACH row that is always there -
 * File, Picture, Voice note - rather than a single button hidden among the
 * tools; a phone's Picture offers its camera and its photos. Dropping files
 * on the panel and pasting a picture land here as well (TodoPanel.js hands
 * them to the same upload). `focusAttach` lands the keyboard on the row,
 * for a row's "Attach…".
 */

/** What a row says about a to-do's checklist and files: "2/3", and a count. */
export function partsOf(item) {
  const lines = Array.isArray(item?.checklist) ? item.checklist : [];
  const files = Array.isArray(item?.attachments) ? item.attachments.length : Number(item?.attachment_count) || 0;
  return { lines: lines.length, ticked: lines.filter((l) => l.done).length, files };
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The row's chips: the checklist's progress and the number of files, each opening the details. */
export function PartsChips({ item, onOpen }) {
  const { lines, ticked, files } = partsOf(item);
  if (!lines && !files) return null;
  const name = item.title ? `"${item.title}"` : 'this to-do';
  return (
    <>
      {lines > 0 && (
        <button type="button" className={`tdp-chip${ticked === lines ? ' is-all' : ''}`} data-checklist-chip={`${ticked}/${lines}`}
                title={`Checklist: ${ticked} of ${lines} ticked`}
                aria-label={`Checklist on ${name}: ${ticked} of ${lines} ticked. Open it`}
                onClick={onOpen}>
          <ChecklistIcon />{ticked}/{lines}
        </button>
      )}
      {files > 0 && (
        <button type="button" className="tdp-chip" data-files-chip={files} title={plural(files, 'file')}
                aria-label={`${plural(files, 'file')} on ${name}. Open them`} onClick={onOpen}>
          <Icon name="paperclip" size={12} />{files}
        </button>
      )}
    </>
  );
}

export default function TodoParts({ item, on, load, download, canRemove = () => true, busy = false, readOnly = false,
                                    focusAttach = false }) {
  const [err, setErr] = useState(null);
  const [edits, setEdits] = useState({});          // line id -> text being typed
  const [draft, setDraft] = useState('');
  const [listOpen, setListOpen] = useState(false);  // "Add a checklist" pressed, before its first line
  const [viewing, setViewing] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInput = useRef(null);
  const imgInput = useRef(null);
  const attachBtn = useRef(null);
  const draftRef = useRef(null);
  // Opened by a row's "Attach…": the keyboard lands on the Attach row - a
  // moment after the panel has taken the focus to its heading, as it does
  // on opening, so this is the focus that stays.
  useEffect(() => {
    if (!focusAttach) return undefined;
    const land = () => {
      const btn = attachBtn.current;
      if (!btn) return;
      // Unless the person has since started typing somewhere: a field keeps
      // its focus; a heading or a row's button does not.
      const a = document.activeElement;
      const typing = a && (/^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) || a.isContentEditable);
      if (!typing && a !== btn) { try { btn.focus({ preventScroll: true }); } catch { /* fine */ } }
    };
    // On the next frame, and once more after the row menu that opened the
    // panel has given the focus back to its row: the Attach row wins both.
    const raf = requestAnimationFrame(land);
    const t1 = setTimeout(land, 80);
    const t2 = setTimeout(land, 260);
    return () => { cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2); };
  }, [focusAttach]);

  const lines = Array.isArray(item.checklist) ? item.checklist : [];
  const imgs = imagesOf(item);
  const voices = audioOf(item);
  const files = (item.attachments || []).filter((a) => !imgs.includes(a) && !voices.includes(a));
  const ticked = lines.filter((l) => l.done).length;
  // The server's words when it sent some; ours otherwise - never axios's own
  // "Request failed with status code 404".
  const fail = (e, words) => setErr((typeof e?.response?.data?.error === 'string' && e.response.data.error) || words);
  const name = item.title ? `"${item.title}"` : 'this to-do';

  const upload = async (list, durationMs = null) => {
    if (!list.length) return;
    setErr(null);
    setSending(true);
    try { await on.upload(item, list, durationMs); } catch (e) { fail(e, 'That could not be added'); }
    finally { setSending(false); }
  };
  const recorder = useVoiceRecorder({
    onRecorded: (file, ms) => upload([file], ms),
    onError: (words) => setErr(words),
    resetKey: item.id
  });

  const addLine = async () => {
    const text = draft.trim();
    if (!text) return;
    if (lines.length >= MAX_LINES) { setErr(`A checklist can have ${MAX_LINES} lines at most`); return; }
    setDraft('');
    setErr(null);
    try { await on.addLine(item, text); setListOpen(false); } catch (e) { setDraft(text); fail(e, 'That line did not go in'); }
    if (draftRef.current) draftRef.current.focus();
  };
  const textOf = (l) => (edits[l.id] !== undefined ? edits[l.id] : l.text);
  const keepText = async (l) => {
    const text = edits[l.id];
    if (text === undefined) return;
    setEdits((prev) => { const n = { ...prev }; delete n[l.id]; return n; });
    if (!text.trim() || text.trim() === l.text) return;
    try { await on.lineText(item, l, text.trim()); } catch (e) { fail(e, 'That line did not save'); }
  };
  const tick = (l, done) => on.tick(item, l, done).catch((e) => fail(e, 'That did not tick'));
  const drop = (l) => on.delLine(item, l).catch((e) => fail(e, 'That line could not be removed'));
  const remove = (a) => on.removeAttachment(item, a).catch((e) => fail(e, 'That could not be removed'));

  const showList = lines.length > 0 || listOpen;
  const hasFiles = imgs.length + voices.length + files.length > 0;
  if (readOnly && !lines.length && !hasFiles) return null;

  return (
    <section className="tdp" data-parts={item.id} aria-label={`Checklist and files on ${name}`}>
      {showList && (
        <div className="tdp-list" data-parts-checklist="">
          <p className="tdp-head">
            <ChecklistIcon />
            <span>Checklist</span>
            {lines.length > 0 && <span className="tdp-count">{ticked} of {lines.length}</span>}
          </p>
          {lines.length > 0 && (
            <span className="tdp-bar" aria-hidden="true">
              <span style={{ width: `${Math.round((ticked / lines.length) * 100)}%` }} />
            </span>
          )}
          <ul className="tdn-lines tdp-lines" aria-label="Checklist">
            {lines.map((l) => (
              <li key={l.id} className={`tdn-line${l.done ? ' is-done' : ''}`} data-line={l.id}>
                <input type="checkbox" checked={Boolean(l.done)} disabled={busy || readOnly}
                       aria-label={l.done ? `Untick "${l.text}"` : `Tick "${l.text}"`}
                       onChange={(e) => tick(l, e.target.checked)} />
                {readOnly ? <span className="tdn-line-text tdp-line-static">{l.text}</span> : (
                  <input type="text" className="tdn-line-text" value={textOf(l)} maxLength={500}
                         aria-label={`Line: ${l.text}`}
                         onChange={(e) => { const v = e.target.value; setEdits((prev) => ({ ...prev, [l.id]: v })); }}
                         onBlur={() => keepText(l)}
                         onKeyDown={(e) => {
                           if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); if (draftRef.current) draftRef.current.focus(); }
                           if (e.key === 'Escape') {
                             e.stopPropagation();
                             setEdits((prev) => { const n = { ...prev }; delete n[l.id]; return n; });
                           }
                         }} />
                )}
                {!readOnly && (
                  <button type="button" className="tdn-remove" aria-label={`Delete line "${l.text}"`} title="Delete"
                          disabled={busy} onClick={() => drop(l)}><Icon name="close" size={14} /></button>
                )}
              </li>
            ))}
            {!readOnly && (
              <li className="tdn-line is-new">
                <span className="tdp-plus" aria-hidden="true"><Icon name="plus" size={14} /></span>
                <input type="text" ref={draftRef} className="tdn-line-text" value={draft} maxLength={500}
                       placeholder="Add an item" aria-label="New checklist item" autoFocus={listOpen && !lines.length}
                       onChange={(e) => setDraft(e.target.value)}
                       onBlur={() => { if (draft.trim()) addLine(); }}
                       onKeyDown={(e) => {
                         if (e.key === 'Enter') { e.preventDefault(); addLine(); }
                         if (e.key === 'Escape' && !lines.length) { e.stopPropagation(); setListOpen(false); setDraft(''); }
                       }} />
              </li>
            )}
          </ul>
        </div>
      )}

      {(hasFiles || !readOnly) && (
        <div className="tdp-files" data-parts-files="">
          {!readOnly && (
            <div className="tdp-attach" data-attach-row="" role="group" aria-label={`Attach to ${name}`}>
              <span className="tdp-attach-label"><Icon name="paperclip" size={13} />Attach</span>
              <button type="button" className="tdp-tool" ref={attachBtn} disabled={busy || sending} data-attach="file"
                      onClick={() => { if (fileInput.current) fileInput.current.click(); }}>
                <Icon name="paperclip" size={14} />{sending ? 'Adding…' : 'File'}
              </button>
              <button type="button" className="tdp-tool" disabled={busy || sending} data-attach="picture"
                      title="A photo or picture - on a phone, the camera or your photos"
                      onClick={() => { if (imgInput.current) imgInput.current.click(); }}>
                <Icon name="image" size={14} />Picture
              </button>
              {canRecord() && !recorder.recording && (
                <button type="button" className="tdp-tool" disabled={busy || sending || recorder.phase !== 'idle'} data-attach="voice"
                        onClick={recorder.start}>
                  <Icon name="mic" size={14} />Voice note
                </button>
              )}
              <input ref={fileInput} type="file" multiple hidden tabIndex={-1} aria-label={`Attach a file to ${name}`} data-attach-input="file"
                     onChange={(e) => {
                       const list = [...(e.target.files || [])];
                       e.target.value = '';
                       upload(list);
                     }} />
              <input ref={imgInput} type="file" accept="image/*" multiple hidden tabIndex={-1} aria-label={`Attach a picture to ${name}`} data-attach-input="picture"
                     onChange={(e) => {
                       const list = [...(e.target.files || [])];
                       e.target.value = '';
                       upload(list);
                     }} />
            </div>
          )}
          {imgs.length > 0 && (
            <div className="tdp-imgs" aria-label="Photos">
              {imgs.map((a) => (
                <span key={a.id} className="tdp-img">
                  <button type="button" className="tdn-img-open" aria-label={`Open ${a.file_name || 'photo'}`}
                          onClick={() => setViewing(a)}>
                    <NoteImage att={a} load={load} />
                  </button>
                  {!readOnly && canRemove(a) && (
                    <button type="button" className="tdn-img-del" aria-label={`Remove ${a.file_name || 'photo'}`}
                            title="Remove photo" disabled={busy} onClick={() => remove(a)}><Icon name="trash" /></button>
                  )}
                </span>
              ))}
            </div>
          )}
          {voices.map((a) => (
            <NoteVoice key={a.id} att={a} load={load} download={download} onError={setErr}
                       onRemove={!readOnly && canRemove(a) ? () => remove(a) : null} />
          ))}
          {files.map((a) => (
            <span key={a.id} className="tdn-file" data-attachment={a.id}>
              <button type="button" className="tdn-file-name" title={`Download ${a.file_name}`}
                      onClick={() => download(a).catch((e) => fail(e, 'Could not download it'))}>
                <Icon name="paperclip" size={13} />
                <span className="tdp-file-text">{a.file_name}</span>
                <span className="tdn-file-size">{fileSize(a.size_bytes)}</span>
              </button>
              {!readOnly && canRemove(a) && (
                <button type="button" className="tdn-remove" aria-label={`Remove ${a.file_name}`} title="Remove"
                        disabled={busy} onClick={() => remove(a)}><Icon name="close" size={14} /></button>
              )}
            </span>
          ))}
        </div>
      )}

      {recorder.recording && <VoiceBar recorder={recorder} />}
      {err && <p className="tdp-err" role="alert">{err}</p>}

      {!readOnly && !showList && (
        <div className="tdp-tools">
          <button type="button" className="tdp-tool" disabled={busy}
                  onClick={() => { setErr(null); setListOpen(true); }}>
            <ChecklistIcon />Add a checklist
          </button>
        </div>
      )}
      {viewing && <ImageViewer att={viewing} load={load} onClose={() => setViewing(null)} />}
    </section>
  );
}
